'use client';

import { mittInstance } from '@/constants/mitt-instance';
import Link from 'next/link';
import React, { ComponentProps, forwardRef, LegacyRef, useState } from 'react';

const safeCreateURL = (url: string, base?: string): URL | null => {
  try {
    return new URL(url, base);
  } catch {
    return null;
  }
};

const isSameHostName = (currentUrl: string, newUrl: string): boolean | null => {
  const current = safeCreateURL(currentUrl, window.location.href);
  const next = safeCreateURL(newUrl, window.location.href);

  if (!current || !next) return null;

  return current.hostname.replace(/^www\./, '') === next.hostname.replace(/^www\./, '');
};

const isHashAnchor = (currentUrl: string, newUrl: string): boolean | null => {
  const current = safeCreateURL(currentUrl, window.location.href);
  const next = safeCreateURL(newUrl, window.location.href);

  if (!current || !next) return null;

  return (
    current.origin === next.origin &&
    current.pathname === next.pathname &&
    current.search === next.search &&
    current.hash !== next.hash
  );
};

const shouldPreventNavigationLoading = (currentUrl: string, targetUrl: string, anchor: HTMLAnchorElement): boolean => {
  const isSpecialScheme = ['tel:', 'mailto:', 'sms:', 'blob:', 'download:'].some(
    scheme => targetUrl.indexOf(scheme) === 0,
  );

  if (isSpecialScheme) return true;

  const isExternalLink = anchor.target && anchor.target !== '_self';

  if (isExternalLink) return true;

  const sameHostNameRet = isSameHostName(currentUrl, targetUrl);

  if (sameHostNameRet === null || !sameHostNameRet) return true;

  const hashAnchorRet = isHashAnchor(currentUrl, targetUrl);

  if (hashAnchorRet === null || hashAnchorRet) return true;

  if (currentUrl === targetUrl) return true;

  const absoluteUrl = safeCreateURL(targetUrl, window.location.href);

  if (!absoluteUrl || absoluteUrl.href.indexOf('http') !== 0) return true;

  return false;
};

const HoverPrefetchLink = forwardRef((props: ComponentProps<typeof Link>, ref: LegacyRef<HTMLAnchorElement>) => {
  const { onMouseEnter, onTouchStart, onClick, ...restProps } = props;

  const [active, setActive] = useState(false);

  const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    const anchor = e.currentTarget as HTMLAnchorElement;
    const newUrl = anchor.href;

    if (!newUrl) return;

    const currentUrl = window.location.href;

    if (e.ctrlKey || e.metaKey || e.shiftKey || e.altKey || (e.nativeEvent && e.nativeEvent.button === 1)) {
      return;
    }

    if (shouldPreventNavigationLoading(currentUrl, newUrl, anchor)) {
      return;
    }

    mittInstance.emit('OPEN_SERVER_NAVIGATE_LOADING');
  };

  return (
    <Link
      prefetch={props.prefetch || active}
      onMouseEnter={e => {
        setActive(true);
        onMouseEnter?.(e);
      }}
      onTouchStart={e => {
        setActive(true);
        onTouchStart?.(e);
      }}
      onClick={e => {
        handleLinkClick(e);
        onClick?.(e);
      }}
      ref={ref}
      {...restProps}
    />
  );
});

export default HoverPrefetchLink;
